import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import countries from "../utils/countries";
import {
  aInputDate,
  cuentaRegresiva,
  diasHasta,
  fechaRecomendadaSugerida,
} from "../utils/fechasEvento";

const TIPOS = ["Evento", "Gira", "Festival"];

// Debajo de este plazo la nota corre riesgo de no llegar. Es la mitad del
// plazo de revisión de una canción (10 días), que es el que van a tener los
// envíos de eventos.
const DIAS_PLAZO_REVISION = 5;

const VACIO = {
  nombre: "",
  tipo: "Evento",
  fecha_inicio: "",
  fecha_fin: "",
  fecha_recomendada: "",
  ciudad: "",
  pais: "",
  lugar: "",
  descripcion: "",
  url_entradas: "",
};

// Alta y edición de un evento. El mismo formulario para los dos puntos de
// entrada: el menú "Eventos" de un perfil individual y la ficha de un artista
// del roster, donde la atribución la resuelve el contexto de la pantalla.
export default function FormEvento({
  abierto,
  evento,
  artista,
  onCerrar,
  onGuardar,
  guardando,
  showSnack,
}) {
  const [form, setForm] = useState(VACIO);
  const [flyer, setFlyer] = useState(null);
  const [preview, setPreview] = useState(null);
  // Mientras el artista no toque la fecha recomendada, sigue a la del evento.
  const [recomendadaTocada, setRecomendadaTocada] = useState(false);

  const esEdicion = !!evento;

  useEffect(() => {
    if (!abierto) return;
    setForm(
      evento
        ? {
            nombre: evento.nombre || "",
            tipo: evento.tipo || "Evento",
            fecha_inicio: aInputDate(evento.fecha_inicio),
            fecha_fin: aInputDate(evento.fecha_fin),
            fecha_recomendada: aInputDate(evento.fecha_recomendada),
            ciudad: evento.ciudad || "",
            pais: evento.pais || "",
            lugar: evento.lugar || "",
            descripcion: evento.descripcion || "",
            url_entradas: evento.url_entradas || "",
          }
        : VACIO
    );
    setFlyer(null);
    setPreview(evento?.flyer || null);
    setRecomendadaTocada(!!evento?.fecha_recomendada);
  }, [abierto, evento]);

  const cambiar = (campo) => (e) => {
    const valor = e.target.value;
    setForm((prev) => {
      const siguiente = { ...prev, [campo]: valor };
      // Al elegir la fecha del evento se precarga la sugerencia de publicación,
      // salvo que ya la hayan puesto a mano.
      if (campo === "fecha_inicio" && !recomendadaTocada)
        siguiente.fecha_recomendada = fechaRecomendadaSugerida(valor);
      return siguiente;
    });
  };

  const elegirFlyer = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFlyer(file);
    setPreview(URL.createObjectURL(file));
  };

  const guardar = () => {
    if (!form.nombre.trim()) {
      showSnack("El nombre del evento es obligatorio.", "warning");
      return;
    }
    if (!form.fecha_inicio) {
      showSnack("La fecha del evento es obligatoria.", "warning");
      return;
    }
    if (!esEdicion && !flyer) {
      showSnack("El flyer del evento es obligatorio.", "warning");
      return;
    }
    if (form.fecha_fin && form.fecha_fin < form.fecha_inicio) {
      showSnack("La fecha de cierre no puede ser anterior a la de inicio.", "warning");
      return;
    }
    onGuardar(form, flyer);
  };

  const opcionesPais = Object.entries(countries).map(([value, label]) => ({
    value,
    label,
  }));

  const dias = diasHasta(form.fecha_inicio);
  // Aviso, no bloqueo: el artista puede tener razones para mandarlo igual.
  const muyCerca = dias !== null && dias >= 0 && dias < DIAS_PLAZO_REVISION;
  const yaPaso = dias !== null && dias < 0;

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="sm">
      <DialogTitle>
        {esEdicion ? `Editar ${evento.nombre}` : "Nuevo evento"}
        {artista && (
          <Typography variant="body2" color="text.secondary">
            de {artista.nombre}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} pt={1}>
          <TextField
            label="Nombre del evento*"
            value={form.nombre}
            onChange={cambiar("nombre")}
            fullWidth
          />

          <TextField
            select
            label="Tipo"
            value={form.tipo}
            onChange={cambiar("tipo")}
            fullWidth
          >
            {TIPOS.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {tipo}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Fecha del evento*"
              type="date"
              value={form.fecha_inicio}
              onChange={cambiar("fecha_inicio")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText={
                form.fecha_inicio ? cuentaRegresiva(form.fecha_inicio) : " "
              }
            />
            <TextField
              label="Hasta (giras)"
              type="date"
              value={form.fecha_fin}
              onChange={cambiar("fecha_fin")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText=" "
            />
          </Stack>

          {yaPaso && (
            <Typography variant="body2" color="error">
              Esa fecha ya pasó. Un evento pasado no se puede enviar a
              difusores.
            </Typography>
          )}
          {muyCerca && (
            <Typography variant="body2" color="warning.main">
              Faltan menos de {DIAS_PLAZO_REVISION} días. El difusor tiene ese
              plazo para responder, así que la nota puede no llegar a tiempo.
            </Typography>
          )}

          <TextField
            label="Fecha recomendada para publicar la nota"
            type="date"
            value={form.fecha_recomendada}
            onChange={(e) => {
              setRecomendadaTocada(true);
              cambiar("fecha_recomendada")(e);
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="Es lo que va a ver el difusor. Se sugiere una semana antes; cámbiala si hay embargo, preventa o un anuncio coordinado."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Ciudad"
              value={form.ciudad}
              onChange={cambiar("ciudad")}
              fullWidth
            />
            <Autocomplete
              options={opcionesPais}
              value={
                opcionesPais.find((opcion) => opcion.value === form.pais) || null
              }
              onChange={(e, opcion) =>
                setForm((prev) => ({ ...prev, pais: opcion?.value || "" }))
              }
              fullWidth
              renderInput={(params) => <TextField {...params} label="País" />}
            />
          </Stack>

          <TextField
            label="Lugar"
            placeholder="Nombre del venue"
            value={form.lugar}
            onChange={cambiar("lugar")}
            fullWidth
          />

          <TextField
            label="Descripción"
            value={form.descripcion}
            onChange={cambiar("descripcion")}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label="Link de entradas"
            placeholder="https://..."
            value={form.url_entradas}
            onChange={cambiar("url_entradas")}
            fullWidth
          />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Flyer{esEdicion ? "" : "*"}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {preview && (
                <Box
                  component="img"
                  src={preview}
                  alt="Flyer"
                  sx={{
                    width: 90,
                    height: 90,
                    objectFit: "contain",
                    borderRadius: 1,
                    border: "1px solid #00000020",
                  }}
                />
              )}
              <Stack spacing={0.5}>
                <Button component="label" size="small">
                  {preview ? "Cambiar flyer" : "Subir flyer"}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={elegirFlyer}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG o WEBP. Se ajusta sin recortarse.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <LoadingButton loading={guardando} variant="contained" onClick={guardar}>
          {esEdicion ? "Guardar cambios" : "Crear evento"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
